"use client";

import { useEffect } from "react";
import { motion } from "framer-motion";
import ServiceCard from "@/components/shared/ServiceCard";
import { getImageUrl } from "@/utils/utils";
import Image from "next/image";

interface ServicesPageClientProps {
  lang: string;
  locale: string;
  data: any;
  loading: boolean;
}

export default function ServicesPageClient({
  lang,
  data,
  loading,
}: ServicesPageClientProps) {
  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "circOut" as const,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5852]"></div>
      </div>
    );
  }

  return (
    <>
      {/* Spectrum of Services Section */}
      <motion.div
        className="bg-[#f4f5f6] pt-12 sm:pt-16 lg:pt-[100px] pb-6 sm:pb-8 lg:pb-[50px]"
        id="spectrum"
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
      >
        <div className="mx-auto">
          <div className="bg-white relative overflow-hidden rounded-lg lg:rounded-none">
            {/* Background Pattern - Hidden on mobile for better performance */}
            <div className="hidden lg:block absolute right-[-1570px] top-[-137.556px] opacity-30 mix-blend-luminosity">
              <svg
                width="2887"
                height="1283.111"
                viewBox="0 0 1318 1014"
                fill="none"
              >
                <g opacity="0.3" clipPath="url(#clip0_1857_13832)">
                  <path
                    d="M2887.5 -137.555H0.5V1145.56H2887.5V-137.555Z"
                    fill="#F0F0F0"
                  />
                  <defs>
                    <clipPath id="clip0_1857_13832">
                      <rect
                        width="2887"
                        height="1283.11"
                        fill="white"
                        transform="translate(0.5 -137.556)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>

            <div className="relative">
              {/* Header */}
              <motion.div
                className="flex flex-col items-center gap-4 lg:gap-[17px] pb-8 sm:pb-12 lg:pb-[50px] px-4 bg-[#f4f5f6]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-[#0a1119] text-center text-2xl sm:text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] lg:tracking-[-1.44px]">
                  {data.spectrumText || "Spectrum of Services"}
                </h2>
                <div className="w-[50px] h-[2px] bg-[#1a5852]"></div>
              </motion.div>
            </div>
          </div>
        </div>
        {data?.spectrumImage && (
          <motion.div
            className="mx-auto p-4 sm:p-6 lg:p-[50px] relative bg-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Image
              src={getImageUrl(data?.spectrumImage?.url || "")}
              alt="Field of Services Background"
              quality={100}
              width={2987}
              height={4096}
              className="object-contain w-full h-auto"
            />
          </motion.div>
        )}
      </motion.div>

      {/* Field of Services Section */}
      <motion.div
        className="bg-[#f4f5f6] pt-6 sm:pt-8 lg:pt-[50px] pb-12 sm:pb-16 lg:pb-[100px]"
        id="field"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={sectionVariants}
      >
        <div className="mx-auto">
          {/* Header - Outside the white box */}
          <motion.div
            className="flex flex-col items-center gap-4 lg:gap-[17px] mb-8 sm:mb-12 lg:mb-[50px]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-[#0a1119] text-center text-2xl sm:text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] lg:tracking-[-1.44px]">
              {data?.fieldOfServices?.fieldOfServices?.title ||
                "Field of Services"}
            </h2>
            <div className="w-[50px] h-[2px] bg-[#1a5852]"></div>
          </motion.div>

          <div className="bg-white relative overflow-hidden rounded-lg lg:rounded-none">
            {/* Background Pattern - Hidden on mobile for better performance */}
            <div className="hidden lg:block absolute right-[-1570px] top-[-235.111px] opacity-30 mix-blend-luminosity">
              <svg
                width="2887"
                height="1283.111"
                viewBox="0 0 1318 1048"
                fill="none"
              >
                <g opacity="0.3" clipPath="url(#clip0_3002_1540)">
                  <path
                    d="M2887.5 0.000244141H0.5V1283.11H2887.5V0.000244141Z"
                    fill="#F0F0F0"
                  />
                  <defs>
                    <clipPath id="clip0_3002_1540">
                      <rect
                        width="2887"
                        height="1283.11"
                        fill="white"
                        transform="translate(0.5)"
                      />
                    </clipPath>
                  </defs>
                </g>
              </svg>
            </div>

            <div className="relative group/progress">
              {/* Services Grid */}
              <div className="p-4 sm:p-6 lg:p-[50px] mx-auto">
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 lg:mb-8"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                >
                  {data?.fieldOfServices?.services?.length > 0 ? (
                    data.fieldOfServices.services.map((service, index) => (
                      <motion.div
                        key={service?.slug || index}
                        variants={cardVariants}
                      >
                        <ServiceCard
                          title={service?.title || `Service ${index + 1}`}
                          excerpt={service?.excerpt || ""}
                          number={service?.number || `${index + 1}`}
                          image={getImageUrl(service?.image?.url)}
                          href={
                            service?.slug
                              ? `/${lang}/services/${service.slug}`
                              : "#"
                          }
                        />
                      </motion.div>
                    ))
                  ) : (
                    <motion.div
                      className="col-span-full text-center py-8 sm:py-12"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      viewport={{ once: true }}
                    >
                      <p className="text-gray-500 text-base sm:text-lg">
                        Services information is currently being updated.
                      </p>
                    </motion.div>
                  )}
                </motion.div>

                {/* Progress Bar */}
                <div className="flex h-[2px] items-center self-stretch bg-[#f4f5f6] relative">
                  <div className="w-0 h-[2px] bg-[#1c8579] absolute left-0 top-0"></div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="flex h-[2px] bg-gray-25 relative">
                <div
                  className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}
